import { useCallback, useState, useRef, ChangeEvent, useMemo } from 'react';
import Hammer from 'react-hammerjs';
import AvatarEditor from 'react-avatar-editor';
import clamp from 'lodash/clamp';
import { nanoid } from 'nanoid';

import { AnyObject } from 'webclient.constants';
import Range from 'lib/form/range/range';
import { RangeContainer } from 'lib/form/range/range.style';
import { ImageState } from 'lib/form/image-picker/image-picker.constants';
import DeleteButtonIcon from 'approot/shared/styles/buttons/delete-icon-button';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import {
  FILL,
  FIT,
  FLIP_HORIZONTAL,
  FlipMode,
  ImageCropData,
  InitialImageSize,
  Mode,
} from '../../image/image-crop.constants';
import { Box } from 'approot/shared/design-system/box/box.style';
import { FlexColumn } from 'approot/shared/styles/flex.style';
import { SecondaryLoadingButton } from 'approot/shared/styles/buttons/loading-button';
import {
  useImageDimensionEffect,
  useImageRequestLoader,
} from 'approot/shared/images/images.hooks';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { getInitialSizeValues } from '../../image/image-crop.utils';
import {
  ImageCropWrapper,
  ImageCropWrapperFit,
} from '../../image/image-crop.style';
import { FileDropperImage } from 'lib/form/file-dropper/file-dropper-image';
import { IMAGE_FILE_KEY_FIELD } from '../image-upload-field';
import {
  ACCEPT_TYPE_IMAGE,
  AcceptTypes,
} from 'lib/form/file-dropper/file-dropper.constants';
import { useFileDropperContext } from 'lib/form/file-dropper/file-dropper.context';
import * as Styled from './component-image-editor.style';
import {
  SettingSmallText,
  SettingSmallTextEllipsis,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';

const randomNumber = nanoid();

type PickerState = {
  pickerWidthPixels: number;
  pickerHeightPixels: number;
  imageReady: boolean;
};

export type Props = {
  imageDimensions: {
    width: number;
    height: number;
  };
  imageFileName?: string;
  imageUrl?: string | null;
  imageKey?: string;
  accept?: AcceptTypes;
  cropValue: ImageCropData | null;
  flipValue?: FlipMode | null;
  canFit?: boolean;
  canFill?: boolean;
  canFlip?: boolean;
  error?: string;
  isDisabled?: boolean;
  disabledTooltip?: string;
  currentImagePreview?: string;
  onCropChange: (crop: ImageCropData | null, mode: Mode) => void;
  onDeleteImage?: () => void;
  onUploadImage: (props: {
    key: string;
    fileName: string;
    original?: string;
    file: File | undefined;
  }) => void;
  onFlipImage?: (flip: FlipMode | null) => void;
};

const ComponentImageEditor = ({
  imageDimensions,
  imageFileName,
  imageUrl,
  imageKey,
  accept,
  cropValue,
  flipValue,
  canFill = true,
  canFit = true,
  canFlip = true,
  error,
  isDisabled,
  disabledTooltip,
  currentImagePreview,
  onCropChange,
  onDeleteImage,
  onUploadImage,
  onFlipImage,
}: Props) => {
  const RANGE_MIN = 1;
  const RANGE_MAX = 5;
  const RANGE_STEP = 0.1;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [isFlipping, setIsFlipping] = useState<boolean>(false);
  const [mode, setMode] = useState<Mode>(!cropValue ? FIT : FILL);
  const [flip, setFlip] = useState<FlipMode | null>(flipValue || null);
  const [imageUrls, setImageUrls] = useState<
    { flip?: string | null; url?: string | null }[]
  >([
    {
      url: imageUrl,
      flip: flipValue || null,
    },
  ]);

  const { clearFile } = useFileDropperContext(IMAGE_FILE_KEY_FIELD);

  const currentImage = imageUrls.find(img => img.flip === flip);

  const elementDimensionLabel = `${
    imageDimensions?.width * 2
  }px x ${imageDimensions?.height * 2}px (W x H)`;

  const [pickerState, setPickerState] = useState<PickerState>({
    pickerWidthPixels: imageDimensions?.width || 300,
    pickerHeightPixels: imageDimensions?.height || 300,
    imageReady: false,
  });

  const [imageState, setImageState] = useState<ImageState>({
    imageWidth: 0,
    imageHeight: 0,
    x: cropValue?.x ?? 0,
    y: cropValue?.y ?? 0,
    zoom: cropValue ? clamp(cropValue.zoom, RANGE_MIN, RANGE_MAX) : 1,
  });

  const scaledDimensions = useMemo(() => {
    const containerWidth = containerRef.current?.offsetWidth || 574;
    const ratio =
      (imageDimensions?.height || 300) / (imageDimensions?.width || 300);
    const calculatedHeight = containerWidth * ratio;
    return scaleDimensions(containerWidth, calculatedHeight);
  }, [imageDimensions]);

  const editor = useRef<AvatarEditor | undefined>(undefined);

  const { getImageResponsive, isLoading: imageResponsiveLoading } =
    useImageRequestLoader({
      url: null,
    });

  const onImageReady = useCallback(() => {
    setPickerState({
      ...pickerState,
      imageReady: true,
    });
  }, [pickerState]);

  const sendNewCropBox = useCallback(
    (
      data: {
        imageWidth: number;
        imageHeight: number;
        x: number;
        y: number;
        zoom: number;
      } | null
    ) => {
      onCropChange(
        data
          ? {
              naturalWidth: data.imageWidth,
              naturalHeight: data.imageHeight,
              x: data.x,
              y: data.y,
              width: pickerState.pickerWidthPixels,
              height: pickerState.pickerHeightPixels,
              zoom: data.zoom,
            }
          : null,
        mode
      );
    },
    [
      pickerState.pickerHeightPixels,
      pickerState.pickerWidthPixels,
      mode,
      onCropChange,
    ]
  );

  const updateZoom = useCallback(
    (newZoom: number) => {
      if (imageState.zoom !== newZoom) {
        setImageState({
          ...imageState,
          zoom: newZoom,
        });
        sendNewCropBox({
          imageWidth: imageState.imageWidth,
          imageHeight: imageState.imageHeight,
          x: imageState.x,
          y: imageState.y,
          zoom: newZoom,
        });
      }
    },
    [imageState, sendNewCropBox]
  );

  const onLoadSuccess = useCallback(
    (imgInfo: AnyObject) => {
      if (!imgInfo.resource) return;

      let zoom: number = 1;
      let newX: number = imageState.x;
      let newY: number = imageState.y;

      if (cropValue) {
        if (cropValue.zoom) {
          zoom = cropValue.zoom;
        }
        const { x, y } = cropValue;
        if (x && y) {
          newX = x;
          newY = y;
        }
      }
      const newState = {
        imageWidth: imgInfo.resource.width,
        imageHeight: imgInfo.resource.height,
        x: newX,
        y: newY,
        zoom: clamp(zoom, RANGE_MIN, RANGE_MAX),
      };

      setImageState(newState);
      sendNewCropBox(newState);
    },
    [cropValue, imageState, sendNewCropBox]
  );

  const onImageChange = useCallback(() => {
    if (pickerState.imageReady && editor?.current?.state?.image) {
      const { x: editorX, y: editorY } = editor.current.state.image;
      const stateX = imageState.x;
      const stateY = imageState.y;

      // We don't want to refresh if values are the same or if === 0.5 (cf comment below)
      if (
        (editorX !== stateX || editorY !== stateY) &&
        (editorX !== 0.5 || editorY !== 0.5)
      ) {
        setImageState({
          ...imageState,
          x: editorX,
          y: editorY,
        });

        const { imageWidth, imageHeight, zoom } = imageState;
        sendNewCropBox({
          imageWidth,
          imageHeight,
          x: editorX,
          y: editorY,
          zoom,
        });
      }
    }
  }, [imageState, pickerState, sendNewCropBox]);

  const handleRangeChange = useCallback(
    (changeEvent: ChangeEvent<HTMLInputElement>) => {
      const zoom = parseFloat(changeEvent.target.value);
      updateZoom(zoom);
    },
    [updateZoom]
  );

  const zoomOut = useCallback(() => {
    const newZoom = clamp(imageState.zoom - RANGE_STEP, RANGE_MIN, RANGE_MAX);
    updateZoom(newZoom);
  }, [imageState.zoom, updateZoom]);

  const zoomIn = useCallback(() => {
    const newZoom = clamp(imageState.zoom + RANGE_STEP, RANGE_MIN, RANGE_MAX);
    updateZoom(newZoom);
  }, [imageState.zoom, updateZoom]);

  const setEditorRef = useCallback((avatarEditor: AvatarEditor) => {
    editor.current = avatarEditor;
  }, []);

  const initialiseImageState = useCallback(
    (size: InitialImageSize | null) => {
      if (size) {
        setImageState({
          imageWidth: size.naturalWidth,
          imageHeight: size.naturalHeight,
          x: cropValue?.x || 0,
          y: cropValue?.y || 0,
          zoom: cropValue?.zoom || 1,
        });
      }
      sendNewCropBox(
        size
          ? {
              imageWidth: size.naturalWidth,
              imageHeight: size.naturalHeight,
              x: cropValue?.x || 0,
              y: cropValue?.y || 0,
              zoom: cropValue?.zoom || 1,
            }
          : null
      );
    },
    [cropValue, sendNewCropBox]
  );

  const renderEditor = useCallback(
    (imageState: ImageState) => {
      const containerRatio = containerRef.current
        ? containerRef.current.offsetWidth / pickerState.pickerWidthPixels
        : 1;

      return (
        <Hammer
          options={{
            recognizers: {
              pinch: { enable: true },
            },
          }}
          onPinchIn={zoomOut}
          onPinchOut={zoomIn}
        >
          <Styled.ImagePickerEditor>
            <AvatarEditor
              border={0}
              ref={setEditorRef}
              key={currentImage?.url || currentImagePreview}
              image={currentImage?.url || currentImagePreview || ''}
              position={{
                x: imageState.x,
                y: imageState.y,
              }}
              width={containerRatio * pickerState.pickerWidthPixels}
              height={containerRatio * pickerState.pickerHeightPixels}
              scale={imageState.zoom}
              style={{
                border: 'none',
                borderRadius: '8px',
                opacity: !pickerState.imageReady ? '0' : '1',
                transition: 'all 300ms 200ms ease',
                width: '100%',
                height: '100%',
              }}
              onPositionChange={onImageChange}
              onImageChange={onImageChange}
              onLoadSuccess={onLoadSuccess}
              onImageReady={onImageReady}
            />
          </Styled.ImagePickerEditor>
        </Hammer>
      );
    },
    [
      currentImage,
      pickerState,
      currentImagePreview,
      onImageChange,
      onImageReady,
      onLoadSuccess,
      setEditorRef,
      zoomIn,
      zoomOut,
    ]
  );

  const handleFlipImage = useCallback(() => {
    if (!imageKey || !onFlipImage) return;

    const flipValue = flip === FLIP_HORIZONTAL ? null : FLIP_HORIZONTAL;

    // if the image has not been flipped yet fetch an image
    if (!imageUrls.find(img => img.flip === flipValue)) {
      setIsFlipping(true);
      getImageResponsive({
        variables: {
          input: {
            imageType: 'content',
            key: imageKey,
            flip: flipValue,
          },
        },
      }).then(res => {
        imageUrls.push({
          url: res.data?.getImageResponsive.original,
          flip: flipValue,
        });
        setImageUrls(...[imageUrls]);
        setFlip(flipValue);
        onFlipImage(flipValue);
        setIsFlipping(false);
      });
    } else {
      setFlip(flipValue);
      onFlipImage(flipValue);
    }
  }, [
    imageKey,
    imageUrls,
    flip,
    getImageResponsive,
    onFlipImage,
    setIsFlipping,
  ]);

  const handleFileChange = useCallback(
    (key: string, fileName: string, file: File | undefined) => {
      getImageResponsive({
        variables: {
          input: {
            imageType: 'content',
            key,
          },
        },
      }).then(res => {
        onUploadImage?.({
          key,
          fileName,
          file,
          original: res.data?.getImageResponsive.original,
        });
        setImageUrls([
          {
            url: res.data?.getImageResponsive.original,
            flip: flip || null,
          },
        ]);
      });
    },
    [flip, setImageUrls, onUploadImage, getImageResponsive]
  );

  const imageLayoutOptions = useMemo(() => {
    const options = [];

    if (canFit) {
      options.push({
        label: 'Fit',
        value: FIT as Mode,
      });
    }
    if (canFill) {
      options.push({
        label: 'Fill',
        value: FILL as Mode,
      });
    }

    return options;
  }, [canFill, canFit]);

  return (
    <div>
      <Box $justifyContent="space-between" $gap={8}>
        <SettingsLabel>Image Upload</SettingsLabel>
        {imageKey && (
          <SettingSmallText marginBottom={0}>
            {elementDimensionLabel}
          </SettingSmallText>
        )}
      </Box>
      <FlexColumn>
        <div
          style={{
            width: scaledDimensions ? scaledDimensions?.width + 'px' : '100%',
          }}
        >
          <Styled.ImagePickerContainer
            ref={containerRef}
            $ratio={scaledDimensions.ratio}
            $hideBorder={!imageKey}
          >
            {!imageKey ? (
              <FileDropperImage
                name={IMAGE_FILE_KEY_FIELD}
                accept={accept ? accept : [ACCEPT_TYPE_IMAGE]}
                showAcceptExtensions={false}
                error={error}
                isFinishing={imageResponsiveLoading}
                additionalLabel={elementDimensionLabel}
                onChange={handleFileChange}
              />
            ) : (
              <>
                {!currentImage ? (
                  <Styled.ImagePickerEditor>
                    <Box $marginTop={24}>
                      <Bouncer />
                    </Box>
                  </Styled.ImagePickerEditor>
                ) : (
                  <>
                    {mode === FIT && currentImage.url ? (
                      <Styled.ImagePickerEditor data-selector="image-picker-container">
                        <ImageViewFit
                          imageUrl={currentImage.url}
                          onLoad={() => initialiseImageState(null)}
                        />
                      </Styled.ImagePickerEditor>
                    ) : (
                      renderEditor(imageState)
                    )}

                    {onDeleteImage && (
                      <Styled.ImagePickerActions>
                        <TooltipWrapper
                          multiline={!!disabledTooltip}
                          hoverText={
                            isDisabled && disabledTooltip
                              ? disabledTooltip
                              : 'Remove Image'
                          }
                        >
                          <DeleteButtonIcon
                            data-selector="delete-image"
                            aria-label={
                              isDisabled && disabledTooltip
                                ? disabledTooltip
                                : 'Remove Image'
                            }
                            onClick={() => {
                              // reset fill and crop values on new upload
                              setFlip(null);
                              onFlipImage?.(null);
                              onCropChange(null, 'fit');
                              clearFile();
                              onDeleteImage();
                            }}
                          />
                        </TooltipWrapper>
                      </Styled.ImagePickerActions>
                    )}
                  </>
                )}
              </>
            )}

            {isDisabled && <Styled.ImagePickerDisabledOverlay />}
          </Styled.ImagePickerContainer>
        </div>
      </FlexColumn>
      {imageKey && (
        <Box $justifyContent="flex-start" $marginBottom={12}>
          <SettingSmallTextEllipsis marginBottom={0}>
            {imageFileName}
          </SettingSmallTextEllipsis>
        </Box>
      )}
      <FlexColumn>
        {imageKey && (
          <>
            <Box
              $gap={8}
              $marginBottom={12}
              $alignItems="center"
              $justifyContent="center"
            >
              <CheckableSingle
                marginBottom={0}
                canWrap={false}
                options={imageLayoutOptions}
                name="mode"
                selected={mode}
                onSelect={value => setMode(value)}
              />
              {canFlip && onFlipImage && (
                <>
                  {(canFit || canFill) && <Styled.ImageOptionsDivider />}
                  <SecondaryLoadingButton
                    type="button"
                    isLoading={isFlipping}
                    disabled={isFlipping || imageResponsiveLoading}
                    onClick={handleFlipImage}
                  >
                    Flip
                  </SecondaryLoadingButton>
                </>
              )}
            </Box>
            {((mode === FILL && pickerState.imageReady) || isDisabled) && (
              <RangeContainer>
                <Range
                  id={randomNumber}
                  handleChange={handleRangeChange}
                  value={isNaN(imageState.zoom) ? RANGE_MIN : imageState.zoom}
                  min={RANGE_MIN}
                  max={RANGE_MAX}
                  isDisabled={isDisabled}
                  step={RANGE_STEP}
                  onDecrement={zoomOut}
                  onIncrement={zoomIn}
                />
                <Styled.ImagePickerHelpText
                  htmlFor={`range-slider-${randomNumber}`}
                >
                  Drag the image to reposition it and use the slider to scale.
                </Styled.ImagePickerHelpText>
              </RangeContainer>
            )}
          </>
        )}
      </FlexColumn>
    </div>
  );
};

export default ComponentImageEditor;

const ImageViewFit = ({
  imageUrl,
  onLoad,
}: {
  imageUrl: string;
  onLoad: (size: InitialImageSize) => void;
}) => {
  const { imageDimensions, setContainerElement } = useImageDimensionEffect({
    imageUrl,
  });
  const isLoading = imageDimensions?.width === undefined;

  return (
    <ImageCropWrapper ref={setContainerElement}>
      <ImageCropWrapperFit>
        <img
          src={imageUrl}
          alt=""
          style={{
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 400ms ease',
            width: imageDimensions?.width + 'px',
            height: imageDimensions?.height + 'px',
          }}
          onLoad={e => onLoad(getInitialSizeValues(e))}
        />
      </ImageCropWrapperFit>
    </ImageCropWrapper>
  );
};

function scaleDimensions(
  width: number,
  height: number,
  maxHeight: number = 300
) {
  if (height > maxHeight) {
    const scalingFactor = maxHeight / height;
    const newWidth = width * scalingFactor;
    return {
      width: newWidth,
      height: maxHeight,
      ratio: maxHeight / newWidth,
    };
  }
  return { width, height, ratio: height / width };
}
